import { useLayoutContext } from '@/contexts';
import { safeAreaTop } from '@/utils';
import { AppShell, Space } from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import { useLocation } from '@tanstack/react-router';
import React, { PropsWithChildren, useMemo } from 'react';
import { BottomNav } from './bottom-nav';
import { DesktopNav } from './desktop-nav/component';
import { Footer, FooterProps } from './footer';
import { Header, HeaderProps } from './header';
import styles from './styles.module.css';
import { TopNav, TopNavFor } from './top-nav';

interface ChildProps {
	width?: number;
	height?: number;
}

export type PrimaryLayoutProps = (
	| {
			header: HeaderProps;
			topNav?: TopNavFor;
	  }
	| {
			header?: never;
	  }
) & {
	footer?: FooterProps;
	context?: 'home' | 'facility';
	backgroundColor?: string;
};

// TODO: Make these defined in just one place
const headerHeight = 85;
const topNavHeight = 60;
const footerHeight = 83;
const contextHeight = 83;

export const PrimaryLayout = (props: PropsWithChildren<PrimaryLayoutProps>) => {
	const { height, width } = useViewportSize();
	const notchPadding = safeAreaTop();
	const { isDesktop, isApp } = useLayoutContext();
	const { pathname } = useLocation();

	const fixedHeaderHeight = props.header
		? props.topNav
			? topNavHeight + headerHeight + notchPadding
			: headerHeight + notchPadding
		: 0;
	const fixedFooterHeight = props.footer
		? props.context
			? contextHeight + footerHeight
			: footerHeight
		: props.context
			? contextHeight
			: 0;

	const showDesktopHeader = useMemo(() => {
		if (!isDesktop) return false;

		switch (pathname) {
			case '/auth/login':
				return false;
			case '/signup':
				return false;
			case '/welcome':
				return false;
			case '/privacy':
				return false;
			case '/terms':
				return false;
			case '/':
				return false;
			default:
				return true;
		}
	}, [pathname, isDesktop]);

	if (isDesktop) {
		return (
			<AppShell className={showDesktopHeader ? styles.withDesktopHeader : styles.appShell}>
				{showDesktopHeader && (
					<AppShell.Header>
						<DesktopNav context={props.context} />
						{props.header && props.topNav && <TopNav forPage={props.topNav} />}
					</AppShell.Header>
				)}
				<AppShell.Main
					className={styles.content}
					style={{ backgroundColor: props.backgroundColor }}
				>
					{props.children}
				</AppShell.Main>
			</AppShell>
		);
	}

	return (
		<AppShell
			header={{ height: fixedHeaderHeight }}
			footer={{ height: fixedFooterHeight }}
			className={styles.appShell}
		>
			{props.header && (
				<div className={styles.fixedHeader}>
					<Space h={safeAreaTop()} />
					<Header {...props.header} />
					{props.topNav && <TopNav forPage={props.topNav} />}
				</div>
			)}
			<div
				className={styles.content}
				style={{ paddingBottom: fixedFooterHeight, backgroundColor: props.backgroundColor }}
			>
				{React.Children.map(props.children, (child) => {
					if (React.isValidElement(child)) {
						return React.cloneElement(child, {
							width,
							height: height - fixedHeaderHeight - fixedFooterHeight,
						} as ChildProps);
					}
					return child;
				})}
			</div>
			{(props.footer || props.context) && (
				<div style={{ paddingBottom: isApp ? '30px' : '0px' }} className={styles.fixedFooter}>
					{props.footer && <Footer {...props.footer} />}
					{props.context && <BottomNav nav={props.context} />}
				</div>
			)}
		</AppShell>
	);
};
